// Requêtes de la rubrique Signalements du portail (D-042) : liste et fiche de suivi de l'usager,
// dépôt, photos une par requête, territoire public de la collectivité pour la carte.
import type { CategorieSignalement, StatutSignalementUsager } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../../api/client';
import type { TerritoireApi } from '../../territoire/donnees';
import { useSessionUsager } from '../session/FournisseurSessionUsager';

export interface PhotoSignalement {
  id: string;
  titre: string;
  type_mime: string;
  taille: number;
}

export interface SignalementUsager {
  id: string;
  numero: string;
  categorie: CategorieSignalement;
  categorie_libelle: string;
  description: string;
  position: { longitude: number; latitude: number } | null;
  adresse: string | null;
  qualification: string;
  qualification_libelle: string;
  statut_usager: { code: StatutSignalementUsager; libelle: string };
  organisme_oriente: string | null;
  principal: { id: string; numero: string | null } | null;
  intervention: { id: string; numero: string | null; etat: string | null } | null;
  photos: PhotoSignalement[];
  qualifie_le: string | null;
  cree_le: string;
  motif: string | null;
  collectivite: { code: string; nom: string };
}

export interface FicheSignalementUsager extends SignalementUsager {
  suivi: { code: string; libelle: string; le: string | null }[];
}

export const CLES_SIGNALEMENTS = {
  liste: ['citoyen', 'signalements'] as const,
  fiche: (id: string) => ['citoyen', 'signalement', id] as const,
  territoire: (code: string) => ['citoyen', 'territoire', code] as const,
};

export function useMesSignalements() {
  const connecte = useSessionUsager().etat === 'connecte';
  return useQuery({
    queryKey: CLES_SIGNALEMENTS.liste,
    queryFn: ({ signal }) => requete<{ elements: SignalementUsager[] }>('/usager/signalements', { signal }).then((r) => r.elements),
    enabled: connecte,
  });
}

export function useSignalementUsager(id: string | null) {
  const connecte = useSessionUsager().etat === 'connecte';
  return useQuery({
    queryKey: CLES_SIGNALEMENTS.fiche(id ?? ''),
    queryFn: ({ signal }) => requete<FicheSignalementUsager>(`/usager/signalements/${id}`, { signal }),
    enabled: connecte && Boolean(id),
    retry: false,
  });
}

/** Territoire public (limites, arrondissements) de la collectivité choisie, pour placer le signalement sur la carte. */
export function useTerritoirePublic(code: string | null) {
  return useQuery({
    queryKey: CLES_SIGNALEMENTS.territoire(code ?? ''),
    queryFn: ({ signal }) => requete<TerritoireApi>(`/public/collectivites/${encodeURIComponent(code as string)}/territoire`, { signal }),
    enabled: Boolean(code),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function deposerSignalement(corps: { collectivite: string; categorie: string; description: string; position: { longitude: number; latitude: number }; adresse?: string }) {
  return requete<SignalementUsager>('/usager/signalements', { methode: 'POST', corps });
}

export function ajouterPhoto(id: string, fichier: File) {
  const donnees = new FormData();
  donnees.append('fichier', fichier);
  return requete<SignalementUsager>(`/usager/signalements/${id}/photos`, { methode: 'POST', corps: donnees });
}

/** Photo d'un signalement de l'usager (servie derrière le jeton) : adresse d'objet locale. */
export async function chargerPhotoUsager(signalementId: string, photoId: string, signal?: AbortSignal): Promise<string> {
  const r = await requete<Response>(`/usager/signalements/${signalementId}/photos/${photoId}`, { brut: true, signal });
  return URL.createObjectURL(await r.blob());
}
